import { useEffect, useState } from 'react'
import { Moon, Sun, Camera, X, Check, Loader2, Percent, BadgeCheck, Wallet } from 'lucide-react'
import type { User as UserType } from '../../lib/types'
import { apiPostJson } from '../../lib/apiClient'
import { unwrapFirst } from '../../lib/apiUnwrap'
import UserAvatar from '../common/UserAvatar'
import { getRankPercent } from '../../lib/rank'

interface ProfileSidebarProps {
  user: UserType
  onUserUpdate?: (user: UserType) => void
  onRefreshProfile?: () => void
}

export default function ProfileSidebar({ user, onUserUpdate, onRefreshProfile }: ProfileSidebarProps) {
  const USER_DISCOUNT_CAP_PERCENT = 5
  const CTV_DISCOUNT_PERCENT = 5

  const [isDark, setIsDark] = useState(false)
  const [showAvatarModal, setShowAvatarModal] = useState(false)
  const [avatarUrl, setAvatarUrl] = useState(user.avatar_url || '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    const stored = localStorage.getItem('digigo-theme')
    const shouldBeDark = stored
      ? stored === 'dark'
      : window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
    setIsDark(shouldBeDark)
    document.documentElement.classList.toggle('dark', shouldBeDark)

    const onThemeChanged = (ev: Event) => {
      const nextIsDark = (ev as CustomEvent<{ isDark: boolean }>).detail?.isDark
      if (typeof nextIsDark === 'boolean') setIsDark(nextIsDark)
    }
    window.addEventListener('digigo-theme-changed', onThemeChanged)
    return () => window.removeEventListener('digigo-theme-changed', onThemeChanged)
  }, [])

  useEffect(() => {
    setAvatarUrl(user.avatar_url || '')
  }, [user.avatar_url])

  const toggleTheme = () => {
    setIsDark(prev => {
      const next = !prev
      localStorage.setItem('digigo-theme', next ? 'dark' : 'light')
      document.documentElement.classList.toggle('dark', next)
      window.dispatchEvent(new CustomEvent('digigo-theme-changed', { detail: { isDark: next } }))
      return next
    })
  }

  const rankLabel = (rank?: UserType['rank']) => {
    switch (rank) {
      case 'dong':
        return 'Đồng'
      case 'bac':
      case 'sat':
        return 'Bạc'
      case 'vang':
        return 'Vàng'
      case 'luc_bao':
        return 'Lục bảo'
      case 'kim_cuong':
        return 'Kim cương'
      default:
        return 'Tân binh'
    }
  }

  const getNextRankInfo = (totalDeposited?: number, rank?: UserType['rank']) => {
    const deposited = totalDeposited || 0

    if (rank === 'kim_cuong' || deposited >= 200000000) {
      return { nextLabel: null as string | null, needed: 0 }
    }

    if (deposited < 500000) return { nextLabel: 'Đồng', needed: 500000 - deposited }
    if (deposited < 2000000) return { nextLabel: 'Bạc', needed: 2000000 - deposited }
    if (deposited < 10000000) return { nextLabel: 'Vàng', needed: 10000000 - deposited }
    if (deposited < 50000000) return { nextLabel: 'Lục bảo', needed: 50000000 - deposited }
    return { nextLabel: 'Kim cương', needed: 200000000 - deposited }
  }

  const handleSaveAvatar = async () => {
    setSaving(true)
    setError('')
    try {
      const resp = await apiPostJson<any>('/api/profile/update-avatar', {
        avatar_url: avatarUrl.trim() || null
      })
      const updatedUser = unwrapFirst<UserType>(resp)
      if (resp?.success) {
        if (updatedUser) {
          onUserUpdate?.(updatedUser)
        }
        onRefreshProfile?.()
        window.dispatchEvent(new Event('digigo-auth-refresh'))
        setShowAvatarModal(false)
      } else {
        setError(resp?.message || 'Cập nhật thất bại')
      }
    } catch (e: any) {
      setError(e?.message || 'Có lỗi xảy ra')
    } finally {
      setSaving(false)
    }
  }

  const nextInfo = getNextRankInfo(user.total_deposited, user.rank)
  const rankDiscountPercent = getRankPercent(user.rank)
  const ctvDiscountPercent = user.is_ctv ? CTV_DISCOUNT_PERCENT : 0
  const memberDiscountPercent = Math.min(rankDiscountPercent + ctvDiscountPercent, USER_DISCOUNT_CAP_PERCENT)
  const commissionBalance = Math.max(0, Number(user.commission_balance || 0))

  return (
    <>
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex flex-col items-center mb-6">
          <div className="relative group">
            <UserAvatar
              src={user.avatar_url}
              name={user.full_name || user.username || user.email}
              alt={user.full_name || user.username || 'Avatar người dùng'}
              containerClassName="h-24 w-24 rounded-full"
              iconClassName="text-3xl"
            />
            <button
              type="button"
              onClick={() => {
                setAvatarUrl(user.avatar_url || '')
                setError('')
                setShowAvatarModal(true)
              }}
              className="absolute bottom-0 right-0 p-1.5 rounded-full bg-amber-600 text-white hover:bg-amber-700 transition-colors shadow-md"
              title="Đổi avatar"
            >
              <Camera className="h-4 w-4" />
            </button>
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mt-4">{user.full_name || 'Người dùng'}</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">{user.email}</p>

          <button
            type="button"
            onClick={toggleTheme}
            className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label={isDark ? 'Chuyển sang chế độ sáng' : 'Chuyển sang chế độ tối'}
            title={isDark ? 'Chế độ tối' : 'Chế độ sáng'}
          >
            {isDark ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
            <span>{isDark ? 'Tối' : 'Sáng'}</span>
          </button>
        </div>

        <div className="space-y-4 border-t pt-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500 dark:text-gray-400">Hạng</span>
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{rankLabel(user.rank)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500 dark:text-gray-400">Cần nạp thêm</span>
            <span className="text-sm text-gray-900 dark:text-gray-100">
              {nextInfo.nextLabel
                ? `${nextInfo.needed.toLocaleString('vi-VN')} đ(lên ${nextInfo.nextLabel})`
                : 'Đã đạt hạng cao nhất'}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500 dark:text-gray-400">Số dư</span>
            <span className="font-bold text-amber-600">{user.balance?.toLocaleString('vi-VN')}đ</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500 dark:text-gray-400">Ngày tham gia</span>
            <span className="text-sm text-gray-900 dark:text-gray-100">
              {new Date(user.created_at).toLocaleDateString('vi-VN')}
            </span>
          </div>
        </div>

        <div className="mt-5 space-y-3 border-t pt-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Ưu đãi của bạn</h3>

          <div className="rounded-lg border border-amber-100 dark:border-amber-900/40 bg-amber-50/70 dark:bg-amber-950/20 p-3 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-700 dark:text-gray-300 inline-flex items-center gap-1.5">
                <Percent className="h-4 w-4 text-amber-600" />
                Giảm giá hạng
              </span>
              <span className="font-semibold text-amber-700 dark:text-amber-300">{rankDiscountPercent}%</span>
            </div>

            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-700 dark:text-gray-300 inline-flex items-center gap-1.5">
                <BadgeCheck className={`h-4 w-4 ${user.is_ctv ? 'text-emerald-600' : 'text-gray-400'}`} />
                Trạng thái CTV
              </span>
              <span className={`font-semibold ${user.is_ctv ? 'text-emerald-600' : 'text-gray-500 dark:text-gray-400'}`}>
                {user.is_ctv ? `Đang bật (+${ctvDiscountPercent}%)` : 'Chưa kích hoạt'}
              </span>
            </div>

            <div className="flex items-center justify-between text-sm pt-2 border-t border-amber-200 dark:border-amber-900/40">
              <span className="text-gray-700 dark:text-gray-300">Giảm giá thành viên hiện tại</span>
              <span className="font-bold text-amber-700 dark:text-amber-300">
                {memberDiscountPercent}% <span className="text-xs font-medium text-gray-500">(tối đa {USER_DISCOUNT_CAP_PERCENT}%)</span>
              </span>
            </div>
          </div>

          <div className="rounded-lg border border-emerald-100 dark:border-emerald-900/40 bg-emerald-50/70 dark:bg-emerald-950/20 p-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700 dark:text-gray-300 inline-flex items-center gap-1.5">
                <Wallet className="h-4 w-4 text-emerald-600" />
                Hoa hồng CTV tích lũy
              </span>
              <span className="text-sm font-bold text-emerald-700 dark:text-emerald-300">
                {commissionBalance.toLocaleString('vi-VN')}đ
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Avatar Modal */}
      {showAvatarModal && (
        <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/50 p-4 pb-[env(safe-area-inset-bottom,0.5rem)] overflow-y-auto" onClick={() => setShowAvatarModal(false)}>
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl p-6 w-full max-w-md my-auto h-[min(32rem,calc(100vh-2rem))] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Đổi Avatar</h3>
              <button onClick={() => setShowAvatarModal(false)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded">
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Ảnh đại diện</label>
                <input
                  type="text"
                  value={avatarUrl}
                  onChange={(e) => setAvatarUrl(e.target.value)}
                  placeholder="https://example.com/avatar.jpg hoặc data:image/...;base64,..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Nhập URL ảnh hoặc chuỗi base64 `data:image/...` (khuyến nghị ảnh vuông)</p>
              </div>

              {avatarUrl && (
                <div className="flex justify-center">
                  <UserAvatar
                    src={avatarUrl}
                    name={user.full_name || user.username || user.email}
                    alt="Xem trước avatar"
                    containerClassName="h-20 w-20 rounded-full"
                    iconClassName="text-2xl"
                  />
                </div>
              )}

              {error && <p className="text-sm text-red-600">{error}</p>}

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowAvatarModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  Hủy
                </button>
                <button
                  type="button"
                  onClick={handleSaveAvatar}
                  disabled={saving}
                  className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                  <span>{saving ? 'Đang lưu...' : 'Lưu'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
